import React from 'react'
import { Link } from 'gatsby'
import styled from '@emotion/styled'

import Route, { RouteKey } from '~/constants/route'
import { PROFILE_LINK_LIST } from '~/constants/profile'

import { useCommonStore } from '~/states/commonStore'

import LinkItem from '../ProfileCard/LinkItem'

import LightLogo from '~/assets/svg/logo-light.inline.svg'
import DarkLogo from '~/assets/svg/logo-dark.inline.svg'

const Footer: React.FC = () => {
  const { isLightMode } = useCommonStore()

  return (
    <Wrapper>
      <Content>
        <LinkWrapper>
          <Link to={Route.main}>
            {isLightMode ? (
              <LightLogo width={120} className="logo" />
            ) : (
              <DarkLogo width={120} className="logo" />
            )}
          </Link>

          <BlogLinkList>
            <li>Blog Links</li>
            {Object.keys(Route).map(routeKey => (
              <li key={routeKey}>
                <Link to={Route[routeKey as RouteKey]}>
                  {routeKey.replace(/\b[a-z]/, letter => letter.toUpperCase())}
                </Link>
              </li>
            ))}
          </BlogLinkList>
        </LinkWrapper>

        <Copyright>
          <div>
            © 2022 1000peach, Powered By <span>Gatsby.</span>
          </div>
          <ProfileLinkList>
            {PROFILE_LINK_LIST.map(link => (
              <li key={link.name}>
                <LinkItem {...link} hasName={false} />
              </li>
            ))}
          </ProfileLinkList>
        </Copyright>
      </Content>
    </Wrapper>
  )
}

const Wrapper = styled.footer`
  position: relative;
  width: 100%;
  margin-top: 50px;
  padding: 40px 0;
  flex: none;
  display: flex;
  text-align: center;
  background-color: ${({ theme: { colors } }) => colors.ghostText};

  @media ${({ theme: { device } }) => device.mobile} {
    margin-top: 30px;
    padding: 30px 0;
  }

  ::before,
  ::after {
    content: '';
    position: absolute;
    top: 0;
    width: ${({ theme: { padding } }) => padding.desktop}px;
    height: 100%;
    background-color: inherit;
  }

  ::before {
    left: -${({ theme: { padding } }) => padding.desktop}px;
  }
  ::after {
    right: -${({ theme: { padding } }) => padding.desktop}px;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: min(100%, ${({ theme: { width } }) => width.main}px);

  @media ${({ theme: { device } }) => device.mobile} {
    flex-direction: column;
    row-gap: 30px;
  }
`

const LinkWrapper = styled.div`
  padding-bottom: 40px;
  display: flex;
  justify-content: space-between;

  @media ${({ theme: { device } }) => device.mobile} {
    flex-direction: column;
    align-items: flex-start;
    row-gap: 20px;
    padding-bottom: 0;
  }
`

const BlogLinkList = styled.ul`
  text-align: left;

  li {
    ${({ theme: { typography } }) => typography.code}
    color: ${({ theme: { colors } }) => colors.darkText}
  }
  li:first-of-type {
    margin-bottom: 5px;
    font-weight: bold;
  }

  @media ${({ theme: { device } }) => device.mobile} {
    margin: 0 0 0 auto;

    li {
      font-size: 13px;
    }
  }
`

const Copyright = styled.div`
  padding-top: 40px;
  border-top: 1px solid ${({ theme: { colors } }) => colors.lightText};
  display: flex;
  justify-content: space-between;

  ${({ theme: { typography } }) => typography.tag1}
  color: ${({ theme: { colors } }) => colors.darkText};
  font-weight: 300;

  @media ${({ theme: { device } }) => device.mobile} {
    padding-top: 30px;
    flex-direction: column;
    justify-content: flex-start;
    row-gap: 15px;
  }
`

const ProfileLinkList = styled.ul`
  display: flex;
  column-gap: 12px;
  align-items: center;

  svg {
    width: 22px;
    height: 22px;
    color: ${({ theme: { colors } }) => colors.darkText};
  }

  @media ${({ theme: { device } }) => device.mobile} {
    justify-content: center;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`

export default Footer
